import { Roles } from '@/entities/roles'
import { USER_ROLE_KEY } from '@/features/auth'
import { useDisputModals } from '@/features/orders/hooks/useDisputModals'
import { useGetDisputData } from '@/features/orders/hooks/useGetDisputData'
import { useGetDisputsList } from '@/features/orders/hooks/useGetDisputsList'
import { AcceptedOrder } from '@/features/orders/ui/dialogs/AcceptedOrder'
import { AcceptOrderDialog } from '@/features/orders/ui/dialogs/AcceptOrderDialog'
import { CanceledOrder } from '@/features/orders/ui/dialogs/CanceledOrder'
import { CancelOrderDialog } from '@/features/orders/ui/dialogs/CancelOrderDialog'
import { OpenOrderDialog } from '@/features/orders/ui/dialogs/OpenOrderDialog'
import { MobileBlocksOrders } from '@/features/orders/ui/mobile/MobileBlocks/MobileBlocksOrders'
import { OrdersInputsBlock } from '@/features/orders/ui/ordersInputsBlock/OrdersInputsBlock'
import { getDisputColumns } from '@/features/orders/ui/ordersTable/columns'
import { BaseButton } from '@/shared/components'
import { PageTitle } from '@/shared/components/PageTitle/PageTitle'
import { DEFAULT_TAKE } from '@/shared/constants/constants'
import { useDebounce } from '@/shared/hooks'
import { getStorageItem } from '@/shared/lib/storage/localStorage'
import { useEffect, useState } from 'react'
import { Plus } from 'lucide-react'
import { CreateDisputeDialog } from '@/features/orders/ui/dialogs/CreateDisputDialog'
import { useCreateDisput } from '@/features/orders/hooks/useCreateDisput'
import { BaseTable, BaseTableSkeleton } from 'syncra-storybook/features'

export const Orders = () => {
  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)
  const [search, setSearch] = useState('')
  const [skip, setSkip] = useState(0)
  const [selectedDisputStatus, setSelectedDisputStatus] = useState(0)
  const [hasError, setHasError] = useState(false)
  const debouncedSearch = useDebounce(search, 500)
  const debouncedSkip = useDebounce(skip, 500)
  const effectiveSkip = skip === 0 ? 0 : debouncedSkip

  const {
    data: queryDisputsData,
    error: disputsError,
    isLoading: isDisputsLoad,
    refetch: refetchDisputs,
  } = useGetDisputsList(
    {
      skip: effectiveSkip,
      take: DEFAULT_TAKE,
      textFilter: debouncedSearch,
      status: selectedDisputStatus,
    },
    {
      enabled: !hasError,
    }
  )

  useEffect(() => {
    if (disputsError) {
      setHasError(true)
    }
  }, [disputsError])

  const { activeDisputId, confirmedAmount, modalSetters, modalStates, handlers } = useDisputModals(
    refetchDisputs,
    !!disputsError
  )

  const { disputData: selectedDisputData } = useGetDisputData({
    disputId: activeDisputId,
  })

  const { handleCreateDisput, isPendingCreate } = useCreateDisput(handlers.closeCreateModal)

  return (
    <div>
      <div className="flex md:flex-row flex-col md:justify-between md:items-center gap-6 mb-4 md:mb-8">
        <PageTitle title="Споры" />
        {userRole === Roles.Merchant && (
          <BaseButton
            variant="primary"
            padding="none"
            radius="md"
            className="px-[30px] w-full md:w-[250px] md:max-w-[250px]"
            iconPosition="left"
            icon={<Plus className="text-white" />}
            onClick={handlers.openCreateModal}
          >
            <span className="hidden md:block">{'Создать новый'}</span>
            <span className="md:hidden">{'Создать'}</span>
          </BaseButton>
        )}
      </div>
      <OrdersInputsBlock
        setSearch={setSearch}
        selectedIndex={selectedDisputStatus}
        selectFilterBtn={(index) => {
          setSelectedDisputStatus(index)
          setSkip(0)
        }}
      />
      {disputsError ? (
        <p>Ошибка загрузки данных.</p>
      ) : isDisputsLoad ? (
        <BaseTableSkeleton />
      ) : !queryDisputsData?.items || queryDisputsData.items.length === 0 ? (
        <p>Споров нет.</p>
      ) : (
        <BaseTable
          columns={getDisputColumns({
            openDisputDialog: handlers.openDisputDialog,
            inProgressDisputDialog: handlers.inProgressDisputDialog,
            userRole,
          })}
          data={queryDisputsData.items}
          totalItems={queryDisputsData?.count}
          skip={skip}
          onPageChange={(newSkip) => setSkip(newSkip)}
          cardClasses="border-0 flex flex-col gap-6 md:gap-8"
          mobileCards={
            <MobileBlocksOrders
              data={queryDisputsData.items}
              openDisputDialog={handlers.openDisputDialog}
              inProgressDisputDialog={handlers.inProgressDisputDialog}
            />
          }
        />
      )}

      {/* Dialogs */}

      <OpenOrderDialog
        disputId={selectedDisputData.id}
        isModalOpen={modalStates.isDisputActionOpen}
        setIsModalOpen={modalSetters.setIsDisputActionOpen}
        onSubmit={handlers.acceptDispute}
        onCancel={handlers.rejectDisput}
      />

      <CreateDisputeDialog
        modalVisible={modalStates.isCreateDisputOpen}
        setModalVisible={modalSetters.setIsCreateDisputOpen}
        onSubmit={handleCreateDisput}
        isLoading={isPendingCreate}
      />

      <AcceptOrderDialog
        amount={confirmedAmount}
        isModalOpen={modalStates.isConfirmAmountOpen}
        setIsModalOpen={modalSetters.setIsConfirmAmountOpen}
        onAction={handlers.confirmAmount}
      />
      <AcceptedOrder
        isModalOpen={modalStates.isAcceptResultOpen}
        onConfirm={handlers.closeAcceptResult}
      />

      <CancelOrderDialog
        isModalOpen={modalStates.isRejectConfirmOpen}
        onClose={modalSetters.setIsRejectConfirmOpen}
        onSubmit={handlers.closeRejectDialog}
      />
      <CanceledOrder
        isModalOpen={modalStates.isRejectResultOpen}
        onConfirm={handlers.closeRejectResult}
      />
    </div>
  )
}
